import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, MapPin, Truck, Heart, HelpCircle, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../lib/database.types';

// Fallback pricing data in case database is unavailable
const fallbackPricingTiers = [
  {
    id: 'fallback-1',
    tier_name: 'Current Community Rate',
    min_subscribers: 0,
    max_subscribers: 49,
    monthly_price: 2100, // $21.00 in cents
    description: 'Current pricing for Marblehead neighbors',
    benefits: ['Free personal delivery', 'Community pricing', 'Cancel anytime'],
    active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'fallback-2',
    tier_name: 'Growing Community',
    min_subscribers: 50,
    max_subscribers: 149,
    monthly_price: 1900, // $19.00 in cents
    description: 'Better pricing as we grow',
    benefits: ['Free personal delivery', 'Community pricing', 'Cancel anytime'],
    active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'fallback-3',
    tier_name: 'Thriving Community',
    min_subscribers: 150,
    max_subscribers: null,
    monthly_price: 1600, // $16.00 in cents
    description: 'Best community pricing',
    benefits: ['Free personal delivery', 'Community pricing', 'Cancel anytime'],
    active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

interface MarbleheadPricingProps {
  roastType: string;
  email: string;
  purchaseType?: 'monthly' | 'single';
  selectedRoast?: string;
  onProceedToOrder: (selectedTier: PricingTier) => void;
  onBack: () => void;
}

type MarbleheadPricingTier = Database['public']['Tables']['marblehead_pricing_tiers']['Row'];

interface PricingTier extends MarbleheadPricingTier {
  benefits: string[];
  selectedRoastType?: string;
  selectedBags?: number;
  deliveryFrequency?: string;
  purchaseType?: 'monthly' | 'single';
}

export default function MarbleheadPricing({ roastType, email, purchaseType: initialPurchaseType = 'monthly', onProceedToOrder, onBack }: MarbleheadPricingProps) {
  const [currentSubscribers, setCurrentSubscribers] = useState(99); // ← CHANGE THIS NUMBER
  const [isLoading, setIsLoading] = useState(true);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [currentStep, setCurrentStep] = useState<'pricing' | 'quantity' | 'delivery'>('pricing');
  const [selectedRoastType, setSelectedRoastType] = useState(roastType);
  const purchaseType = initialPurchaseType; // Don't allow changing purchase type
  const [showCommunityPricingModal, setShowCommunityPricingModal] = useState(false);
  const [selectedBags, setSelectedBags] = useState(1);
  const [deliveryFrequency, setDeliveryFrequency] = useState<'all-at-once' | 'spread-out'>('all-at-once');
  const [pickupMethod, setPickupMethod] = useState<'delivery' | 'local-noon'>('delivery');

  const currentTier = pricingTiers.find(tier =>
    currentSubscribers >= tier.min_subscribers && 
    (tier.max_subscribers === null || currentSubscribers <= tier.max_subscribers)
  ) || pricingTiers[0];

  const nextTier = pricingTiers.find(tier => tier.min_subscribers > currentSubscribers);
  const subscribersNeeded = nextTier ? nextTier.min_subscribers - currentSubscribers : 0;

  useEffect(() => {
    loadPricingTiers();
  }, []);

  const loadPricingTiers = async () => {
    let caughtError: any = null;
    let data: any = null;
    setIsLoading(true);
    
    try {
      const response = await supabase
        .from('marblehead_pricing_tiers')
        .select('*')
        .eq('active', true)
        .order('min_subscribers');
      
      data = response.data;
      caughtError = response.error;
      
      if (caughtError) throw caughtError;
      
      // Transform the data to include parsed benefits
      const transformedTiers: PricingTier[] = (data || []).map((tier: any) => ({
        ...tier,
        benefits: Array.isArray(tier.benefits) ? tier.benefits as string[] : []
      }));
      
      setPricingTiers(transformedTiers);
    } catch (err) {
      caughtError = err;
      if (data && data.length > 0) {
        // Transform the data to include parsed benefits
        const transformedTiers: PricingTier[] = data.map((tier: any) => ({
          ...tier,
          benefits: Array.isArray(tier.benefits) ? tier.benefits as string[] : []
        }));
        setPricingTiers(transformedTiers);
      } else {
        // Use fallback if no data returned
        setPricingTiers(fallbackPricingTiers);
      }
    } finally {
      setIsLoading(false);
      if (caughtError) {
        console.warn('Error loading Marblehead pricing tiers:', caughtError);
      }
    }
  };

  const roastEmojis = {
    light: '🕊️',
    medium: '🌿',
    dark: '🌋',
    espresso: '🚀'
  };

  const roastInfo = {
    light: {
      emoji: '🕊️',
      title: 'Light Roast',
      description: 'Bright, zippy, citrusy. Like drinking sunshine.'
    },
    medium: {
      emoji: '🌿',
      title: 'Medium Roast',
      description: 'Smooth, balanced, crowd-pleasing. The Switzerland of coffee.'
    },
    dark: {
      emoji: '🌋',
      title: 'Dark Roast',
      description: 'Rich, smoky, bold. A little dark and slightly bitter.'
    },
    espresso: {
      emoji: '🚀',
      title: 'Espresso',
      description: 'Intense, rich, and concentrated. Not here to sip—here to feel.'
    }
  };

  const specificRoasts = [
    {
      id: 'regiment-roast',
      name: 'Regiment Roast',
      description: 'Community. Craft. Showing up.',
      emoji: '⚓',
      available: true
    },
    {
      id: 'wake-with-drake',
      name: 'Wake with Drake',
      description: 'For football mornings and family traditions',
      emoji: '🏈',
      available: false
    },
    {
      id: 'barnegat-breakfast',
      name: 'Barnegat Breakfast',
      description: 'Our signature morning blend',
      emoji: '🌅',
      available: true
    },
    {
      id: 'soleless-shakedown',
      name: 'Soleless Shakedown',
      description: 'Bold and mischievous',
      emoji: '⚡',
      available: true
    },
    {
      id: 'nunnepog-fog',
      name: 'Nunnepog Fog',
      description: 'Gentle and contemplative',
      emoji: '🌫️',
      available: true
    }
  ];

  const handleProceedToOrder = () => {
    if (!currentTier) return;
    
    const tierWithSelections = {
      ...currentTier,
      selectedRoastType,
      purchaseType,
      selectedBags,
      deliveryFrequency,
      pickupMethod
    };
    
    onProceedToOrder(tierWithSelections);
  };

  const CommunityPricingModal = () => {
    if (!showCommunityPricingModal) return null;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-[#132947] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-[#132947] p-6 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-2xl font-bold text-[#fda36b]">Community Pricing Explained</h3>
            <button
              onClick={() => setShowCommunityPricingModal(false)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-8 h-8 text-[#fda36b]" />
                <h4 className="text-xl font-bold text-white">How Community Pricing Works</h4>
              </div>
              
              <p className="text-white/90 leading-relaxed">
                As neighbors in Marblehead, we believe in the power of community. The more people who join Bond Coffee, 
                the better we can make the prices for everyone. It's like a neighborhood co-op, but for really good coffee.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-6 rounded-lg border border-[#fda36b]/20">
              <h4 className="text-lg font-bold text-[#fda36b] mb-4">Current Pricing Tiers</h4>
              <div className="space-y-3">
                {pricingTiers.map((tier, index) => (
                  <div 
                    key={tier.id} 
                    className={`p-4 rounded-lg border ${
                      tier === currentTier 
                        ? 'border-[#fda36b] bg-[#fda36b]/10' 
                        : 'border-white/20 bg-white/5'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-white">
                          {tier.tier_name}
                          {tier === currentTier && (
                            <span className="ml-2 text-xs bg-[#fda36b] text-[#132947] px-2 py-1 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="text-white/70 text-sm">
                          {tier.min_subscribers} - {tier.max_subscribers || '∞'} neighbors
                        </div>
                      </div>
                      <div className="text-xl font-bold text-[#fda36b]">
                        ${(tier.monthly_price / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6 text-[#fda36b]" />
                <h4 className="text-lg font-bold text-white">Personal Delivery</h4>
              </div>
              <p className="text-white/90 leading-relaxed">
                Every Marblehead order gets personal delivery by Adam and Chris themselves. 
                No shipping fees, no waiting for packages - just fresh coffee delivered right to your door, 
                probably in our Japanese mini truck.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-[#fda36b]" />
                <h4 className="text-lg font-bold text-white">Growing Together</h4>
              </div>
              <p className="text-white/90 leading-relaxed">
                Every time a new neighbor joins, we all benefit. More volume means better prices from our suppliers, 
                which we pass directly to the community. It's not just about coffee - it's about building something 
                together in Marblehead.
              </p>
            </div>

            <div className="bg-white/5 p-4 rounded-lg border border-white/10">
              <p className="text-white/80 text-sm text-center leading-relaxed">
                <span className="font-semibold text-[#fda36b]">Current Status:</span> We have {currentSubscribers} neighbors 
                in our community. {nextTier ? `Just ${subscribersNeeded} more and everyone saves to $${(nextTier.monthly_price / 100).toFixed(2)}!` : 'We\'ve reached our best pricing tier!'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#fda36b] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-white/80">Loading community pricing...</p>
      </div>
    );
  }

  if (!currentTier) {
    return (
      <div className="text-center py-12">
        <p className="text-white/80">Unable to load pricing tiers. Please try again.</p>
      </div>
    );
  }

  // Step 1: Community Pricing Overview
  if (currentStep === 'pricing') {
    const deliveryPrice = currentTier.monthly_price / 100;
    const pickupPrice = 16;
    const savings = deliveryPrice - pickupPrice;

    return (
      <>
        <div className="space-y-6">
        {/* Marblehead Community Pricing Header */}
        <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-5 rounded-2xl border border-[#fda36b]/20 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <MapPin className="w-5 h-5 text-[#fda36b]" />
            <h2 className="text-xl font-bold text-white">Marblehead Community Pricing</h2>
          </div>
          <p className="text-white/70 text-sm">
            {currentSubscribers} neighbors and growing
          </p>
          {nextTier && (
            <p className="text-white/60 text-xs mt-1">
              Only <span className="font-semibold text-[#fda36b]">{subscribersNeeded} more neighbors</span> and we all drop to ${(nextTier.monthly_price / 100).toFixed(2)}!
            </p>
          )}
          <button
            onClick={() => setShowCommunityPricingModal(true)}
            className="inline-flex items-center gap-1.5 text-[#fda36b] hover:text-[#fda36b]/80 text-xs underline transition-colors mt-2"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Learn more about community pricing
          </button>
        </div>

        {/* Two-option price comparison */}
        {purchaseType === 'monthly' && (
          <>
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-1">Choose How You'd Like to Receive It</h3>
              <p className="text-white/50 text-sm">Pick up downtown and save ${savings.toFixed(0)} a month</p>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
              {/* Home Delivery */}
              <button
                onClick={() => setPickupMethod('delivery')}
                className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-center flex flex-col items-center gap-3 ${
                  pickupMethod === 'delivery'
                    ? 'border-white/50 bg-white/10'
                    : 'border-white/20 bg-white/5 hover:border-white/30'
                }`}
              >
                {pickupMethod === 'delivery' && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-[#132947] rounded-full" />
                  </div>
                )}
                <div className="text-3xl">🚚</div>
                <div>
                  <p className="font-bold text-white text-sm mb-0.5">Home Delivery</p>
                  <p className="text-white/50 text-xs">To your door, free</p>
                </div>
                <div className="mt-auto pt-1">
                  <div className="text-3xl font-bold text-white">${deliveryPrice.toFixed(0)}</div>
                  <div className="text-white/50 text-xs">per month</div>
                </div>
              </button>

              {/* Local Noon Pickup */}
              <button
                onClick={() => setPickupMethod('local-noon')}
                className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-center flex flex-col items-center gap-3 ${
                  pickupMethod === 'local-noon'
                    ? 'border-[#fda36b] bg-[#fda36b]/15 ring-2 ring-[#fda36b]/30'
                    : 'border-[#fda36b]/50 bg-[#fda36b]/5 hover:border-[#fda36b]/80'
                }`}
              >
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#fda36b] text-[#132947] text-xs font-black px-3 py-1 rounded-full whitespace-nowrap shadow-lg">
                  SAVE ${savings.toFixed(0)}/MONTH
                </div>
                {pickupMethod === 'local-noon' && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-[#fda36b] rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-[#132947] rounded-full" />
                  </div>
                )}
                <div className="text-3xl mt-1">🏪</div>
                <div>
                  <p className="font-bold text-[#fda36b] text-sm mb-0.5">Pick Up at Local Noon</p>
                  <p className="text-white/50 text-xs">92 Washington St, downtown</p>
                </div>
                <div className="mt-auto pt-1">
                  <div className="text-3xl font-bold text-[#fda36b]">${pickupPrice}</div>
                  <div className="text-white/50 text-xs">per month</div>
                </div>
              </button>
            </div>

            {/* Detail blurb for selected option */}
            <div className={`max-w-2xl mx-auto rounded-xl p-4 border text-sm transition-all duration-300 ${
              pickupMethod === 'local-noon'
                ? 'bg-[#fda36b]/10 border-[#fda36b]/30 text-white/70'
                : 'bg-white/5 border-white/10 text-white/70'
            }`}>
              {pickupMethod === 'local-noon'
                ? "Amy's beach & ski lifestyle shop on Washington Street. We drop your fresh bag there each month — a great excuse to come downtown. Mon–Sat 10AM–5:30PM · Sun 11AM–4PM"
                : "Adam and Chris deliver personally to your door in Marblehead — free, no shipping, probably in the Japanese mini truck."
              }
            </div>
          </>
        )}

        {purchaseType === 'single' && (
          <div className="text-center space-y-3">
            <h3 className="text-xl font-bold text-white">Single Order</h3>
            <p className="text-white/70">You'll choose a specific roast for a one-time purchase.</p>
          </div>
        )}

        <div className="flex gap-4 justify-center">
          <button
            onClick={() => purchaseType === 'monthly' ? setCurrentStep('quantity') : handleProceedToOrder()}
            className="bg-[#fda36b] text-[#132947] px-8 py-3 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center gap-2"
          >
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
        </div>

        <CommunityPricingModal />
      </>
    );
  }

  // Step 2: Bag Quantity Selection (Monthly Only)
  if (currentStep === 'quantity') {
    return (
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <div className="text-6xl mb-4">📦</div>
          <h2 className="text-3xl font-bold text-white">
            How Many Bags Per Month?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Perfect for households with multiple coffee drinkers or if you just really love great coffee!
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
          {(pickupMethod === 'local-noon' ? [1] : [1, 2]).map((bags) => (
            <button
              key={bags}
              onClick={() => setSelectedBags(bags)}
              className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                selectedBags === bags
                  ? 'border-[#fda36b] bg-[#fda36b]/10'
                  : 'border-white/20 bg-white/5 hover:border-[#fda36b]/50'
              }`}
            >
              <div className="text-center space-y-3">
                <div className="text-4xl">
                  {bags === 1 ? '☕' : '☕☕'}
                </div>
                <div className="text-xl font-bold text-white">
                  {bags} Bag{bags > 1 ? 's' : ''}
                </div>
                <div className="text-[#fda36b] font-semibold">
                  ${pickupMethod === 'local-noon' ? (16 * bags).toFixed(2) : ((currentTier.monthly_price * bags) / 100).toFixed(2)}/month
                </div>
                <div className="text-white/70 text-sm">
                  {bags === 1 ? 'Perfect for 1-2 people' : pickupMethod === 'local-noon' ? 'Pick up both at Local Noon' : "All at once or spread out! You'll tell us on the next screen."}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="bg-white/5 p-4 rounded-lg border border-white/10 max-w-2xl mx-auto">
          <p className="text-white/80 text-sm text-center leading-relaxed">
            Each bag is 12oz of freshly roasted coffee. You can always change your quantity later!
          </p>
        </div>

        <div className="flex gap-4 justify-center">
          <button
            onClick={() => setCurrentStep('pricing')}
            className="bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300"
          >
            Back
          </button>
          <button
            onClick={() => pickupMethod === 'local-noon' ? handleProceedToOrder() : setCurrentStep('delivery')}
            className="bg-[#fda36b] text-[#132947] px-6 py-3 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center gap-2"
          >
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Step 3: Delivery / Pickup Selection (Monthly Only)
  if (currentStep === 'delivery') {
    const deliveryPrice = currentTier.monthly_price / 100;
    const pickupPrice = 16;
    const savings = deliveryPrice - pickupPrice;

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-white">
            How Would You Like to Get Your Coffee?
          </h2>
          <p className="text-white/60 text-base">Two options — one saves you ${savings.toFixed(0)} a month</p>
        </div>

        {/* Side-by-side price comparison */}
        <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
          {/* Home Delivery */}
          <button
            onClick={() => setPickupMethod('delivery')}
            className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-center flex flex-col items-center gap-3 ${
              pickupMethod === 'delivery'
                ? 'border-white/50 bg-white/10'
                : 'border-white/20 bg-white/5 hover:border-white/30'
            }`}
          >
            {pickupMethod === 'delivery' && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-white rounded-full flex items-center justify-center shrink-0">
                <div className="w-2 h-2 bg-[#132947] rounded-full" />
              </div>
            )}
            <div className="text-3xl">🚚</div>
            <div>
              <p className="font-bold text-white text-sm mb-1">Home Delivery</p>
              <p className="text-white/50 text-xs">To your door in Marblehead</p>
            </div>
            <div className="mt-auto pt-2">
              <div className="text-3xl font-bold text-white">${deliveryPrice.toFixed(0)}</div>
              <div className="text-white/50 text-xs">per month</div>
            </div>
          </button>

          {/* Local Noon Pickup */}
          <button
            onClick={() => setPickupMethod('local-noon')}
            className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-center flex flex-col items-center gap-3 ${
              pickupMethod === 'local-noon'
                ? 'border-[#fda36b] bg-[#fda36b]/15 ring-2 ring-[#fda36b]/30'
                : 'border-[#fda36b]/50 bg-[#fda36b]/5 hover:border-[#fda36b]/80'
            }`}
          >
            {/* Save badge */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#fda36b] text-[#132947] text-xs font-black px-3 py-1 rounded-full whitespace-nowrap shadow-lg">
              SAVE ${savings.toFixed(0)}/MONTH
            </div>
            {pickupMethod === 'local-noon' && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-[#fda36b] rounded-full flex items-center justify-center shrink-0">
                <div className="w-2 h-2 bg-[#132947] rounded-full" />
              </div>
            )}
            <div className="text-3xl mt-1">🏪</div>
            <div>
              <p className="font-bold text-[#fda36b] text-sm mb-1">Pick Up at Local Noon</p>
              <p className="text-white/50 text-xs">92 Washington St, downtown</p>
            </div>
            <div className="mt-auto pt-2">
              <div className="text-3xl font-bold text-[#fda36b]">${pickupPrice}</div>
              <div className="text-white/50 text-xs">per month</div>
            </div>
          </button>
        </div>

        {/* Selected option detail */}
        <div className={`max-w-2xl mx-auto rounded-2xl p-5 border transition-all duration-300 ${
          pickupMethod === 'local-noon'
            ? 'bg-[#fda36b]/10 border-[#fda36b]/30'
            : 'bg-white/5 border-white/10'
        }`}>
          {pickupMethod === 'local-noon' ? (
            <div className="space-y-1.5">
              <p className="font-semibold text-white text-sm">About Local Noon</p>
              <p className="text-white/70 text-sm leading-relaxed">
                Amy's beach and ski lifestyle shop right on Washington Street. We drop your fresh bag there each month — a great excuse to come downtown, browse, and say hi.
              </p>
              <p className="text-white/40 text-xs pt-1">Mon–Sat 10AM–5:30PM · Sun 11AM–4PM</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <p className="font-semibold text-white text-sm">About Home Delivery</p>
              <p className="text-white/70 text-sm leading-relaxed">
                Adam and Chris deliver personally to your door in Marblehead — free, no shipping, probably in the Japanese mini truck.
              </p>
              {selectedBags > 1 && (
                <div className="pt-3 space-y-2 border-t border-white/10 mt-2">
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-wide">Delivery Schedule</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setDeliveryFrequency('all-at-once')}
                      className={`p-3 rounded-xl border text-left transition-all text-xs ${
                        deliveryFrequency === 'all-at-once'
                          ? 'border-white/50 bg-white/10 text-white'
                          : 'border-white/20 bg-white/5 text-white/60 hover:border-white/30'
                      }`}
                    >
                      <div className="font-semibold mb-0.5">All at once</div>
                      <div className="text-white/50">Both bags together</div>
                    </button>
                    <button
                      onClick={() => setDeliveryFrequency('spread-out')}
                      className={`p-3 rounded-xl border text-left transition-all text-xs ${
                        deliveryFrequency === 'spread-out'
                          ? 'border-white/50 bg-white/10 text-white'
                          : 'border-white/20 bg-white/5 text-white/60 hover:border-white/30'
                      }`}
                    >
                      <div className="font-semibold mb-0.5">Spread out</div>
                      <div className="text-white/50">Throughout the month</div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary line */}
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-4 rounded-xl border border-[#fda36b]/20 text-center">
          <span className="text-white/70 text-sm">Your plan: </span>
          <span className="text-white font-semibold text-sm">{selectedBags} bag{selectedBags > 1 ? 's' : ''}/month</span>
          <span className="text-white/40 text-sm"> · </span>
          <span className="text-[#fda36b] font-bold text-sm">
            {pickupMethod === 'local-noon'
              ? `$${pickupPrice * selectedBags}/month — Local Noon pickup`
              : `$${(deliveryPrice * selectedBags).toFixed(0)}/month — home delivery`}
          </span>
        </div>

        <div className="flex gap-4 justify-center">
          <button
            onClick={() => setCurrentStep('quantity')}
            className="bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300"
          >
            Back
          </button>
          <button
            onClick={handleProceedToOrder}
            className="bg-[#fda36b] text-[#132947] px-8 py-3 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center gap-2"
          >
            Continue to Order
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}